import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Home, Search, Heart, Clock, User, LogOut, Music2 } from "lucide-react";
import { useEffect } from "react";
import { useAuth } from "@/stores/auth";
import { PlayerBar } from "./PlayerBar";
import { usePlayer } from "@/stores/player";
import { cn } from "@/lib/utils";

const tabs = [
  { to: "/home", label: "Home", icon: Home },
  { to: "/search", label: "Search", icon: Search },
  { to: "/favorites", label: "Favorites", icon: Heart },
  { to: "/history", label: "History", icon: Clock },
  { to: "/profile", label: "Profile", icon: User },
];

export function AppShell() {
  const { user, initialized, init, logout } = useAuth();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const hasPlayer = usePlayer((s) => !!s.current);

  useEffect(() => {
    if (!initialized) init();
  }, [initialized, init]);

  useEffect(() => {
    if (initialized && !user) navigate({ to: "/login" });
  }, [initialized, user, navigate]);

  if (!initialized || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-60 flex-col border-r border-border bg-sidebar">
        <Link to="/home" className="flex items-center gap-2 px-6 py-6">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
            <Music2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="text-xl font-semibold tracking-tight">Sura</span>
        </Link>
        <nav className="flex-1 px-3 space-y-1">
          {tabs.map((t) => {
            const active = path === t.to || (t.to !== "/home" && path.startsWith(t.to));
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                  active
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                )}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <button
            onClick={logout}
            className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm text-muted-foreground hover:text-destructive hover:bg-secondary"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 bg-background/90 backdrop-blur border-b border-border">
        <div className="px-4 h-14 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
              <Music2 className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-semibold">Sura</span>
          </Link>
          <Link to="/profile" className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-sm font-medium">
            {user.full_name?.[0]?.toUpperCase() || "U"}
          </Link>
        </div>
      </header>

      <main
        className={cn(
          "md:ml-60",
          hasPlayer ? "pb-32 md:pb-24" : "pb-20 md:pb-6",
        )}
      >
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav
        className={cn(
          "md:hidden fixed left-0 right-0 z-30 bg-card/95 backdrop-blur border-t border-border",
          hasPlayer ? "bottom-[72px]" : "bottom-0",
        )}
      >
        <div className="grid grid-cols-5">
          {tabs.map((t) => {
            const active = path === t.to || (t.to !== "/home" && path.startsWith(t.to));
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                className={cn(
                  "flex flex-col items-center gap-1 py-2 text-[10px]",
                  active ? "text-primary" : "text-muted-foreground",
                )}
              >
                <Icon className="h-5 w-5" />
                {t.label}
              </Link>
            );
          })}
        </div>
      </nav>

      <PlayerBar />
    </div>
  );
}
